import { Fragment, useEffect, useMemo, useState } from 'react'
import {
  Info,
  SelectMenu,
  Typography,
  GoalGraph,
  GraphLegends,
  LoadingHavaianas,
  BarGraph,
  MultiselectMenu,
  SlimButton,
  Tooltip,
  Icon
} from 'components'
import { Skeleton } from '@mui/material'
import { useDispatch, useSelector, shallowEqual } from 'store/store'
import { numberToDecimalString } from 'commons/utils/number'
import { toAllFirstLetterUpper } from 'commons/utils/string'

import {
  getDashboard,
  getFilterOptions,
  getCategoryReport,
  getMetaReport,
  setSelectedQuota,
  getQuotaMonthOptions
} from 'store/HomeContext/homeSlice'
import { StrategyProduct } from 'services/home/interfaces'

import * as S from './styles'
import { formatDate } from 'commons/utils/date'
import { Channels, Offices } from 'constants/enums'
import { useCommercialStructure } from 'commons/customHooks'
import { getCommercialStructure } from 'store/SharedContext/sharedSlice'
import { UserPreferences } from 'services/login/interfaces'

export const Dashboard = () => {
  const dispatch = useDispatch()

  const {
    loadingDashboard,
    loadingOptions,
    loadingMetaReport,
    loadingCategoryReport,
    loadingQuotaOptions,
    selectedQuota,
    quotaOptions,
    dashboardData
  } = useSelector((state) => state.home, shallowEqual)

  const {
    offices: officesCommercialStructure,
    regionals: regionalsCommercialStructure,
    zones: zonesCommercialStructure
  } = useSelector((state) => state.shared)

  const { getMarkets, getOfficesByMarkets, getRegionalsByOffices, getZonesByRegionals } =
    useCommercialStructure()

  const legends = [
    { label: 'Até 49%', fill: '#FF4842' },
    { label: 'De 50% até 84%', fill: '#FF8D23' },
    { label: 'De 85% até 99%', fill: '#FFC107' },
    { label: 'Acima de 100%', fill: '#54D62C' }
  ]

  const materialPrefix = 'SANDALIAS HAVAIANAS '

  const categoryLabelsMap: Record<string, string> = {
    FEMININA: 'Feminina',
    MASCULINA: 'Masculina',
    'KIDS&BABY': 'Kids e Baby',
    UNISSEX: 'Unissex',
    ESTRATEGICO: 'Estratégico',
    PHASEOUT: 'Phase Out',
    OLIMPÍADAS: 'Olimpíadas',
    BRANDEXTENSION: 'Brand Extension',
    SELEÇÃOESPECIAL: 'Seleção Especial'
  }

  const calcPercent = (value: number, goal: number) => {
    if (goal > 0) return (value / goal) * 100
    return 0
  }

  useEffect(() => {
    dispatch(getCommercialStructure())
    dispatch(getFilterOptions())
  }, [])

  const metaPercent = useMemo(() => {
    const { VolumeTotal, VolumeAprovado } = dashboardData.CarteiraTotal
    return calcPercent(VolumeAprovado, VolumeTotal)
  }, [dashboardData])

  const [selectedMarkets, setSelectedMarkets] = useState<string[]>([])
  const [selectedOffices, setSelectedOffices] = useState<string[]>([])
  const [selectedRegionals, setSelectedRegionals] = useState<string[]>([])
  const [selectedZones, setSelectedZones] = useState<string[]>([])

  const preferences = JSON.parse(
    localStorage.getItem('preferences') || '{}'
  ) as UserPreferences
  const {
    CanalDistribuicao: prefMarkets,
    EscritorioVenda: prefOffices,
    EquipeVenda: prefRegionals,
    RegiaoVenda: prefZones
  } = preferences

  const disableSearch = useMemo(() => {
    return (
      selectedMarkets.length === 0 ||
      selectedOffices.length === 0 ||
      selectedRegionals.length === 0 ||
      selectedZones.length === 0 ||
      loadingDashboard ||
      loadingOptions
    )
  }, [
    selectedMarkets,
    selectedOffices,
    selectedRegionals,
    selectedZones,
    loadingDashboard,
    loadingOptions
  ])

  const restoreCommercialStructure = () => {
    setSelectedMarkets(prefMarkets ? [prefMarkets] : markets.map((market) => market.id))
    setSelectedOffices(prefOffices ? [prefOffices] : offices.map((office) => office.id))
    setSelectedRegionals(
      prefRegionals ? [prefRegionals] : regionals.map((regional) => regional.id)
    )
    setSelectedZones(prefZones ? [prefZones] : zones.map((zone) => zone.id))
  }

  useEffect(() => {
    restoreCommercialStructure()
  }, [officesCommercialStructure, regionalsCommercialStructure, zonesCommercialStructure])

  const markets = useMemo(() => {
    return getMarkets()
  }, [selectedMarkets, officesCommercialStructure])

  const offices = useMemo(() => {
    const officesByMarket = getOfficesByMarkets(selectedMarkets as Channels[])

    if (selectedMarkets.length && prefMarkets) {
      const asSameAsPrefMarket = selectedMarkets.every((market) =>
        [prefMarkets].includes(market)
      )

      if (asSameAsPrefMarket && prefOffices) {
        setSelectedOffices([prefOffices] as string[])
        return officesByMarket
      }
    }

    setSelectedOffices(officesByMarket.map((office) => office.id))

    return officesByMarket
  }, [selectedMarkets, officesCommercialStructure])

  const regionals = useMemo(() => {
    const regionalsByOffices = getRegionalsByOffices(selectedOffices as Offices[])

    if (selectedOffices.length && prefOffices) {
      const asSameAsPrefOffice = selectedOffices.every((office) =>
        [prefOffices].includes(office)
      )

      if (asSameAsPrefOffice && prefRegionals) {
        setSelectedRegionals([prefRegionals] as string[])
        return regionalsByOffices
      }
    }

    setSelectedRegionals(regionalsByOffices.map((regional) => regional.id))

    return regionalsByOffices
  }, [selectedOffices, regionalsCommercialStructure])

  const zones = useMemo(() => {
    const zonesByRegionals = getZonesByRegionals(selectedRegionals)

    if (selectedRegionals.length && prefRegionals) {
      const asSameAsPrefRegional = selectedRegionals.every((regional) =>
        [prefRegionals].includes(regional)
      )

      if (asSameAsPrefRegional && prefZones) {
        setSelectedZones([prefZones] as string[])
        return zonesByRegionals
      }
    }

    setSelectedZones(zonesByRegionals.map((zone) => zone.id))

    return zonesByRegionals
  }, [selectedRegionals, zonesCommercialStructure])

  const handleQuotaSelect = (value: string) => {
    dispatch(setSelectedQuota(value))
  }

  const handleOfficeChange = (value: string[]) => {
    setSelectedOffices(value)

    if (value.length) {
      dispatch(getQuotaMonthOptions(value))
    }
  }

  const formattedQuota = formatDate(selectedQuota, 'YYYYMM', 'MM/YY')

  const handleMetaReport = () => {
    dispatch(
      getMetaReport(
        {
          AnoMesCota: formattedQuota,
          EscritorioVenda: selectedOffices,
          Regional: selectedRegionals,
          Zona: selectedZones
        },
        regionals.length,
        zones.length
      )
    )
  }

  const handleCategoryReport = () => {
    dispatch(
      getCategoryReport(
        {
          AnoMesCota: formattedQuota,
          EscritorioVenda: selectedOffices,
          Regional: selectedRegionals,
          Zona: selectedZones
        },
        regionals.length,
        zones.length
      )
    )
  }

  const handleSearch = () => {
    dispatch(
      getDashboard({
        AnoMesCota: formattedQuota,
        EscritorioVenda: selectedOffices,
        Regional: selectedRegionals.length === regionals.length ? [] : selectedRegionals,
        Zona: selectedZones.length === zones.length ? [] : selectedZones
      })
    )
  }

  const handleClear = () => {
    dispatch(getFilterOptions())
    restoreCommercialStructure()
  }

  const renderSkeleton = () => {
    const items = Array.from({ length: 5 }, (_, index) => ({
      id: index + 1
    }))

    return items.map((item, index) => {
      const shouldRenderDivider = index !== 0

      return (
        <Fragment key={`${item.id}`}>
          {shouldRenderDivider && <S.VertDivider />}
          <S.DataCol>
            <Skeleton width={80} />
            <S.DataNumber>
              <Skeleton width={40} />
              <Skeleton width={60} />
            </S.DataNumber>
          </S.DataCol>
        </Fragment>
      )
    })
  }

  const products = dashboardData.ProdutosEstrategicos

  const renderStrategyItems = (products: StrategyProduct[]) => {
    return products.map((product) => {
      return (
        <S.DataCol key={`${product.Codigo}`} className="grid-item">
          <S.DataNumber>
            <Typography design="hav-h8-text">
              {toAllFirstLetterUpper(product.Descricao.replace(materialPrefix, ''))}
            </Typography>
            <Typography design="hav-default-05-text">Código: {product.Codigo}</Typography>
          </S.DataNumber>
          <span>
            <Typography design="hav-h7-text">
              {numberToDecimalString(product.VolumeAprovado)}{' '}
            </Typography>
            <Typography design="hav-default-04-text">
              / {numberToDecimalString(product.VolumeTotal)}
            </Typography>
          </span>
          <BarGraph
            percentValue={calcPercent(product.VolumeAprovado, product.VolumeTotal)}
          />
        </S.DataCol>
      )
    })
  }

  return (
    <S.Wrapper>
      <S.TitleCard>
        <S.TitleRow>
          <Typography design="hav-h5-text">Gestão da carteira</Typography>
          <S.FiltersRow>
            <MultiselectMenu
              label="Mercado"
              selectedOptions={selectedMarkets}
              onChange={(value) => {
                setSelectedMarkets(value)
              }}
              optionsList={markets.map((market) => ({
                label: market.description,
                value: market.id
              }))}
              required
              hasBorder
              disabled={loadingOptions || loadingDashboard}
              clearAction={() => {
                setSelectedMarkets([])
                handleOfficeChange([])
                setSelectedRegionals([])
                setSelectedZones([])
              }}
              checkAllAction={() =>
                setSelectedMarkets(markets.map((market) => market.id))
              }
            />
            <MultiselectMenu
              label="Escritório"
              selectedOptions={selectedOffices}
              onChange={(value) => {
                handleOfficeChange(value)
              }}
              optionsList={offices.map((office) => ({
                label: `${office.id} - ${office.description}`,
                value: office.id
              }))}
              required
              hasBorder
              disabled={loadingOptions || loadingDashboard}
              clearAction={() => {
                handleOfficeChange([])
                setSelectedRegionals([])
                setSelectedZones([])
              }}
              checkAllAction={() =>
                handleOfficeChange(offices.map((office) => office.id))
              }
            />
            <MultiselectMenu
              label="Regional"
              selectedOptions={selectedRegionals}
              onChange={(value) => {
                setSelectedRegionals(value)
              }}
              optionsList={regionals.map((regional) => ({
                label: `${regional.id} - ${regional.description}`,
                value: regional.id
              }))}
              required
              hasSearch
              hasBorder
              searchPlaceholder="Buscar regional"
              disabled={loadingOptions || loadingDashboard}
              clearAction={() => {
                setSelectedRegionals([])
                setSelectedZones([])
              }}
              checkAllAction={() =>
                setSelectedRegionals(regionals.map((regional) => regional.id))
              }
            />
            <MultiselectMenu
              label="Zona"
              selectedOptions={selectedZones}
              onChange={(value) => {
                setSelectedZones(value)
              }}
              optionsList={zones.map((zone) => ({
                label: `${zone.id} - ${zone.description}`,
                value: zone.id
              }))}
              required
              hasSearch
              hasBorder
              searchPlaceholder="Buscar zona"
              disabled={loadingOptions || loadingDashboard}
              clearAction={() => setSelectedZones([])}
              checkAllAction={() => setSelectedZones(zones.map((zone) => zone.id))}
            />
            <SelectMenu
              label="Mês cota"
              options={quotaOptions}
              value={selectedQuota}
              onChange={handleQuotaSelect}
              disabled={loadingOptions || loadingDashboard || loadingQuotaOptions}
              hasBorder
            />
            <SlimButton
              variant="primary"
              content="Buscar"
              onClick={handleSearch}
              disabled={disableSearch || loadingQuotaOptions}
            />
            <Tooltip title="Redefinir filtros" placement="top" arrow>
              <div>
                <SlimButton
                  id="home-filters-clear-button"
                  variant="secondary"
                  onClick={handleClear}
                  disabled={loadingOptions || loadingDashboard || loadingQuotaOptions}
                  icon={<Icon icon="clear-all" size="20" />}
                />
              </div>
            </Tooltip>
          </S.FiltersRow>
        </S.TitleRow>
      </S.TitleCard>
      <S.Content>
        <S.SectionCard className="carteira">
          <S.TitleRow>
            <S.SectionTitle>
              <Typography design="hav-h6-text">Carteira total & Meta</Typography>
              <Info iconSize={15}>
                Acompanhe o seu desempenho em relação à meta de vendas.
              </Info>
            </S.SectionTitle>
            {loadingDashboard ? (
              <Skeleton width={75} />
            ) : (
              <S.ActionLink loading={loadingMetaReport}>
                <span onClick={handleMetaReport}>Ver detalhes</span>
              </S.ActionLink>
            )}
          </S.TitleRow>
          <S.ResultsWrapper>
            {loadingDashboard ? (
              <S.GraphLoading>
                <LoadingHavaianas loadingText="" />
              </S.GraphLoading>
            ) : (
              <S.GraphRow>
                <GoalGraph percentValue={metaPercent} />
                <GraphLegends data={legends} />
              </S.GraphRow>
            )}
            <S.ResultsCol>
              <S.HozDivider />
              <S.TextRow>
                <Typography design="hav-h8-text">Volume da meta</Typography>
                <Typography design="hav-h7-text">
                  {loadingDashboard ? (
                    <Skeleton width={75} />
                  ) : (
                    numberToDecimalString(dashboardData.CarteiraTotal.VolumeTotal)
                  )}
                </Typography>
              </S.TextRow>
              <S.HozDivider />
              <S.TextRow>
                <Typography design="hav-h8-text">Aprovados</Typography>
                <Typography design="hav-h7-text">
                  {loadingDashboard ? (
                    <Skeleton width={75} />
                  ) : (
                    numberToDecimalString(dashboardData.CarteiraTotal.VolumeAprovado)
                  )}
                </Typography>
              </S.TextRow>
              <S.TextRow>
                <Typography design="hav-h8-text">Digitados</Typography>
                <Typography design="hav-h7-text">
                  {loadingDashboard ? (
                    <Skeleton width={50} />
                  ) : (
                    numberToDecimalString(dashboardData.CarteiraTotal.VolumeDigitado)
                  )}
                </Typography>
              </S.TextRow>
              <S.TextRow>
                <Typography design="hav-h8-text">Meta restante</Typography>
                <Typography design="hav-h7-text">
                  {loadingDashboard ? (
                    <Skeleton width={60} />
                  ) : (
                    numberToDecimalString(dashboardData.CarteiraTotal.VolumeRestante)
                  )}
                </Typography>
              </S.TextRow>
            </S.ResultsCol>
          </S.ResultsWrapper>
        </S.SectionCard>

        <S.SectionCard className="cestas">
          <S.TitleCol>
            <S.TitleRow>
              <S.SectionTitle>
                <Typography design="hav-h6-text">Acompanhamento por categoria</Typography>
                <Info iconSize={15}>
                  Categorias de produtos que compõem a sua meta. Essa segmentação é usada
                  para calcular a remuneração variável e garantir a diversidade de linhas
                  dentro da carteira.
                </Info>
              </S.SectionTitle>
              {loadingDashboard ? (
                <Skeleton width={150} />
              ) : (
                <S.ActionLink loading={loadingCategoryReport}>
                  <span onClick={handleCategoryReport}>Ver composição de cestas</span>
                </S.ActionLink>
              )}
            </S.TitleRow>
            <Typography design="hav-default-04-text">
              Valores exibidos em pares
            </Typography>
          </S.TitleCol>
          <S.DataRow>
            {loadingDashboard
              ? renderSkeleton()
              : dashboardData.CestasDeProdutos.map((category, index) => {
                  return (
                    <Fragment key={`${category.Nome}`}>
                      {index !== 0 && <S.VertDivider />}
                      <S.DataCol>
                        <Typography design="hav-h8-text">
                          {categoryLabelsMap[category.Nome]}
                        </Typography>
                        <span>
                          <Typography design="hav-h7-text">
                            {numberToDecimalString(category.VolumeAprovado)}{' '}
                          </Typography>
                          <Typography design="hav-default-04-text">
                            / {numberToDecimalString(category.VolumeTotal)}
                          </Typography>
                        </span>
                        <BarGraph
                          percentValue={calcPercent(
                            category.VolumeAprovado,
                            category.VolumeTotal
                          )}
                        />
                      </S.DataCol>
                    </Fragment>
                  )
                })}
          </S.DataRow>
        </S.SectionCard>

        <S.SectionCard className="produtos">
          <S.TitleCol>
            <S.SectionTitle>
              <Typography design="hav-h6-text">Produtos estratégicos & Meta</Typography>
              <Info iconSize={15}>
                Produtos estratégicos com maior potencial de margem e grande oferta. O
                volume estratégico aprovado dentro da sua carteira compõe a apuração da
                remuneração variável.
              </Info>
            </S.SectionTitle>

            <Typography design="hav-default-04-text">
              Valores exibidos em pares
            </Typography>
          </S.TitleCol>
          {loadingDashboard ? (
            <S.DataRow>{renderSkeleton()}</S.DataRow>
          ) : (
            <S.StrategyGrid>{renderStrategyItems(products)}</S.StrategyGrid>
          )}
        </S.SectionCard>
      </S.Content>
    </S.Wrapper>
  )
}
